import { z } from 'zod';
export const ParticipantType = z.enum(['manufacturer', 'importer', 'installer', 'retailer', 'aggregator']);
export const ParticipantStatus = z.enum(['pending', 'approved', 'suspended', 'rejected', 'withdrawn']);
export const ApplicationStatus = z.enum(['draft', 'submitted', 'under_assessment', 'returned', 'approved', 'rejected', 'withdrawn']);
export const Address = z.object({
    /** First address line. @metadata business */
    line1: z.string().min(1).max(200),
    /** Second address line, if any. @metadata business */
    line2: z.string().max(200).optional(),
    /** Suburb or locality. @metadata business */
    suburb: z.string().min(1).max(100),
    /** State or territory. @metadata business */
    state: z.enum(['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA']),
    /** Four-digit postcode. @metadata business */
    postcode: z.string().regex(/^\d{4}$/, 'Postcode must be 4 digits'),
});
/** Organisation facts supplied by the applicant and maintained on the participant record. */
export const ParticipantDetails = z.object({
    /** Registered legal name of the organisation. @metadata business @source register.participants.legal_name */
    legalName: z.string().min(2).max(200),
    /** Trading name, if different from the legal name. @metadata business @source register.participants.trading_name @transform null is omitted */
    tradingName: z.string().max(200).optional(),
    /** Australian Business Number (11 digits). @metadata business @source register.participants.abn */
    abn: z.string().regex(/^\d{11}$/, 'ABN must be 11 digits'),
    /** Role in the scheme: manufacturer, importer, installer, retailer or aggregator. @metadata business @source register.participants.participant_type */
    participantType: ParticipantType,
    /** Name of the organisation's contact person. @metadata business @source register.participants.contact_name */
    contactName: z.string().min(2).max(200),
    /** Email address of the contact person. @metadata business @source register.participants.contact_email */
    contactEmail: z.string().email(),
    /** Phone number of the contact person. @metadata business @source register.participants.contact_phone */
    contactPhone: z.string().min(6).max(30),
    /** Postal address of the organisation. @metadata business @source register.participants.address @transform parsed through Address */
    address: Address,
});
export const Participant = ParticipantDetails.extend({
    /** Surrogate id of the participant. @metadata technical @source register.participants.id */
    id: z.string(),
    /** Registration status: pending, approved, suspended, rejected or withdrawn. @metadata business @source register.participants.status */
    status: ParticipantStatus,
    /** Optimistic-concurrency version, incremented on every change. @metadata technical @source register.participants.version */
    version: z.number().int(),
    /** When the participant was created. @metadata technical @source register.participants.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
    /** When the participant was last changed. @metadata technical @source register.participants.updated_at @transform timestamptz as an ISO 8601 UTC string */
    updatedAt: z.string(),
});
export const ParticipantUpdate = z.object({
    details: ParticipantDetails.partial(),
    status: z.enum(['approved', 'suspended']).optional(),
    changeReason: z.string().min(3).max(1000),
    validFrom: z.string().datetime({ offset: true }).optional(),
    correctionOf: z.number().int().positive().optional(),
});
export const ParticipantSearch = z.object({
    q: z.string().max(200).optional(),
    status: ParticipantStatus.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const ParticipantVersion = z.object({
    versionId: z.number().int(),
    version: z.number().int(),
    validFrom: z.string(),
    validTo: z.string().nullable(),
    recordedAt: z.string(),
    changedBy: z.string().nullable(),
    changedByName: z.string().nullable(),
    changeReason: z.string().nullable(),
    sourceChannel: z.string(),
    correctionOf: z.number().int().nullable(),
    supersededBy: z.number().int().nullable(),
    data: Participant.omit({ version: true, createdAt: true, updatedAt: true }),
});
export const ParticipantHistory = z.object({
    participantId: z.string(),
    versions: z.array(ParticipantVersion),
});
export const AsOfQuery = z.object({
    asOf: z.string().datetime({ offset: true }).optional(),
    knownAt: z.string().datetime({ offset: true }).optional(),
});
export const ApplicationDeclarations = z.object({
    /** Applicant confirmed they are authorised to apply for the organisation. @metadata business */
    authorisedToApply: z.literal(true, { errorMap: () => ({ message: 'You must confirm you are authorised to apply' }) }),
    /** Applicant declared the information supplied is true. @metadata business */
    informationTrue: z.literal(true, { errorMap: () => ({ message: 'You must declare the information is true' }) }),
    /** Applicant's notes on the evidence supplied. @metadata business */
    evidenceNotes: z.string().max(2000).optional(),
});
export const ApplicationSubmission = z.object({
    details: ParticipantDetails,
    declarations: ApplicationDeclarations,
});
export const ApplicationDraft = z.object({
    details: ParticipantDetails,
    declarations: ApplicationDeclarations.partial().optional(),
});
export const ApplicationNote = z.object({
    /** Surrogate id of the note. @metadata technical @source register.application_notes.id */
    id: z.string(),
    /** Kind of note: assessment, return, decision or applicant. @metadata business @source register.application_notes.kind */
    kind: z.enum(['assessment', 'return', 'decision', 'applicant']),
    /** User who wrote the note. @metadata technical @source register.application_notes.author_id */
    authorId: z.string().nullable(),
    /** Display name of the note's author. @metadata technical @source iam.users.display_name via register.application_notes.author_id @transform left join; null if the user is not found */
    authorName: z.string().nullable(),
    /** Text of the note. @metadata business @source register.application_notes.body */
    body: z.string(),
    /** When the note was written. @metadata technical @source register.application_notes.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
});
export const Application = z.object({
    /** Surrogate id of the application. @metadata technical @source register.participant_applications.id */
    id: z.string(),
    /** Id of the participant the application is for. @metadata technical @source register.participant_applications.participant_id */
    participantId: z.string(),
    /** User who made the application. @metadata technical @source register.participant_applications.applicant_user_id */
    applicantUserId: z.string(),
    /** Application status: draft, submitted, under_assessment, returned, approved, rejected or withdrawn. @metadata business @source register.participant_applications.status */
    status: ApplicationStatus,
    /** Declarations made by the applicant. @metadata business @source register.participant_applications.declarations @transform parsed through ApplicationDeclarations (all optional); null becomes {} */
    declarations: ApplicationDeclarations.partial(),
    /** When the application was last submitted. @metadata business @source register.participant_applications.submitted_at @transform timestamptz as an ISO 8601 UTC string */
    submittedAt: z.string().nullable(),
    /** CER user assessing the application. @metadata technical @source register.participant_applications.assessor_id */
    assessorId: z.string().nullable(),
    /** Display name of the assessor. @metadata technical @source iam.users.display_name via register.participant_applications.assessor_id @transform left join; null if there is no assessor */
    assessorName: z.string().nullable(),
    /** When the decision was made. @metadata business @source register.participant_applications.decided_at @transform timestamptz as an ISO 8601 UTC string */
    decidedAt: z.string().nullable(),
    /** CER's decision: approved or rejected; null until decided. @metadata business @source register.participant_applications.decision */
    decision: z.enum(['approved', 'rejected']).nullable(),
    /** Reason given for the decision. @metadata business @source register.participant_applications.decision_reason */
    decisionReason: z.string().nullable(),
    /** When the application was started. @metadata technical @source register.participant_applications.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
    /** When the application was last changed. @metadata technical @source register.participant_applications.updated_at @transform timestamptz as an ISO 8601 UTC string */
    updatedAt: z.string(),
    /** The participant record the application is for, in its participant contract shape. @metadata business @source derived: register.participants row for participant_id */
    participant: Participant,
    /** Assessment, return, decision and applicant notes, oldest first. @metadata business @source derived: register.application_notes rows for the application @transform ordered by created_at */
    notes: z.array(ApplicationNote),
});
export const ApplicationSummary = Application.omit({ notes: true, participant: true }).extend({
    legalName: z.string(),
    abn: z.string(),
    participantType: ParticipantType,
});
export const ApplicationList = z.object({ items: z.array(ApplicationSummary) });
export const ParticipantList = z.object({ items: z.array(Participant) });
export const ApplicationDecision = z.object({
    decision: z.enum(['approve', 'reject', 'return']),
    reason: z.string().min(10, 'Give a reason of at least 10 characters').max(2000),
});
export const ApplicationNoteInput = z.object({ body: z.string().min(1).max(4000) });
//# sourceMappingURL=participants.js.map